import { useCallback } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import { useStartAPetitionPageState } from '#src/app/pages/startAPetitionV2/pageContext';

type FormData = { recommendation: string };

type UseRecommendationsReturn = ModelHookResult<
	{
		recommendations: Array<{ id: string; recommendation: string; why: string }>;
		defaultRecommendationId: string;
	},
	{
		submit: (formData: FormData) => void;
		back: () => void;
	}
>;

export function useRecommendations(): UseRecommendationsReturn {
	const { data, actions } = useStartAPetitionPageState();
	const track = useTracking();

	const submit = useCallback(
		(formData: FormData) => {
			const { recommendation, type, position, why } = (() => {
				const aiRecommendations = data.draft?.input?.recommendations || [];
				const [index, aiRecommendation] =
					[...aiRecommendations.entries()].find(([, rec]) => rec.id === formData.recommendation) ?? [];
				if (index !== undefined && aiRecommendation)
					return { ...aiRecommendation, type: 'recommendation', position: index + 1 };

				return {
					type: 'write_your_own',
					recommendation: 'write_your_own',
					why: '',
					position: aiRecommendations.length + 1,
				};
			})();

			void track('sap_recommendations_submit', { ai_recommended_angle: { type, position, recommendation, why } });

			if (recommendation === 'write_your_own') {
				actions.send({
					type: 'submit',
					target: 'user',
					draft: { petitionDraft: null, recommendedDraft: null },
				});

				return;
			}

			const selectedRecommendation = recommendation;
			actions.send({ type: 'submit', target: 'ai', draft: { input: { selectedRecommendation } } });
		},
		[track, data.draft, actions],
	);

	const back = useCallback(() => {
		void track('sap_recommendations_back');
		actions.send({ type: 'back' });
	}, [track, actions]);

	return {
		data: {
			recommendations: data.draft?.input?.recommendations || [],
			defaultRecommendationId: data.draft?.input?.recommendations?.[0]?.id || '',
		},
		actions: { submit, back },
	};
}
